import { useEffect, useRef, useState } from "react";
import {
  Link,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { sendEmailVerification, signOut } from "firebase/auth";
import {
  Check,
  Leaf,
  Plus,
  Mail,
  GitBranch,
  ArrowLeft,
  Users,
} from "lucide-react";
import { api, auth, friendlyError, patch, post, requestId } from "./api";
import { useAuth } from "./state";
import { useI18n } from "./i18n";
import { Arrow, Brand, Button, ErrorBox, Field, Heading, Loading } from "./ui";
import { LanguageButton } from "./public";
import { PRIMARY_COLORS, SECONDARY_COLORS, themeVariables } from "./theme";
import { layoutTree } from "./tree-layout";
import type { Person, Relationship, Workspace } from "./types";
export function Protected() {
  const { user, loading } = useAuth();
  const { t, language } = useI18n();
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [, rerender] = useState(0);
  if (loading) return <Loading />;
  if (!user)
    return <Navigate to={language === "he" ? "/he/login" : "/login"} replace />;
  if (!user.emailVerified)
    return (
      <div className="setup-shell">
        <SetupHeader />
        <main className="verify-card">
          <Mail size={40} strokeWidth={1} />
          <h1>
            {t(
              "A small step to keep things private.",
              "צעד קטן לשמירה על הפרטיות.",
            )}
          </h1>
          <p>
            {t(
              "Open the verification email sent to",
              "פתחו את הודעת האימות שנשלחה אל",
            )}{" "}
            <b dir="ltr">{user.email}</b>.{" "}
            {t(
              "Once verified, you can create or join your family workspace.",
              "לאחר האימות, תוכלו ליצור מרחב משפחתי או להצטרף למרחב קיים.",
            )}
          </p>
          <ErrorBox error={error} />
          {notice && (
            <p role="status" className="notice">
              {notice}
            </p>
          )}
          <Button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await user.reload();
                await user.getIdToken(true);
                if (!user.emailVerified)
                  setNotice(
                    t(
                      "Your email is not verified yet. Check your inbox, then try again.",
                      "כתובת הדוא״ל טרם אומתה. בדקו את תיבת הדואר ונסו שוב.",
                    ),
                  );
                rerender((n) => n + 1);
              } catch (e) {
                setError(friendlyError(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("I have verified my email", "אימתתי את כתובת הדוא״ל")}
            <Arrow />
          </Button>
          <button
            className="text-link"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await sendEmailVerification(user);
                setNotice(t("Verification email sent.", "הודעת האימות נשלחה."));
              } catch (e) {
                setError(friendlyError(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("Send another email", "שליחת הודעה נוספת")}
          </button>
          <button className="text-link" onClick={() => void signOut(auth)}>
            {t("Sign out", "יציאה")}
          </button>
        </main>
      </div>
    );
  return <Outlet />;
}
export function SetupHeader() {
  const { t } = useI18n();
  return (
    <header className="setup-header">
      <Link to="/app">
        <Brand />
      </Link>
      <div>
        <LanguageButton />
        <button className="text-link" onClick={() => void signOut(auth)}>
          {t("Sign out", "יציאה")}
        </button>
      </div>
    </header>
  );
}
export function WorkspaceChooser() {
  const { workspaces, refresh, error } = useAuth();
  const { t } = useI18n();
  const token = sessionStorage.getItem("heritage-invitation");
  useEffect(() => {
    void refresh().catch(() => {});
  }, [refresh]);
  if (token)
    return (
      <Navigate to={`/invite?token=${encodeURIComponent(token)}`} replace />
    );
  if (!error && workspaces.length === 0)
    return <Navigate to="/app/new" replace />;
  return (
    <div className="setup-shell">
      <SetupHeader />
      <main className="chooser">
        <Heading
          eyebrow={t("YOUR PRIVATE SPACES", "המרחבים הפרטיים שלכם")}
          title={t("A place for every family.", "מקום לכל משפחה.")}
          description={t(
            "Choose a workspace to continue your story.",
            "בחרו מרחב כדי להמשיך את הסיפור שלכם.",
          )}
        />
        <ErrorBox error={error} retry={() => void refresh().catch(() => {})} />
        <div className="workspace-grid">
          {workspaces.map((workspace) => (
            <Link
              className="workspace-card"
              style={themeVariables(
                workspace.primaryColor,
                workspace.secondaryColor,
              )}
              key={workspace.id}
              to={
                workspace.status === "initializing"
                  ? `/app/setup/${workspace.id}`
                  : `/app/w/${workspace.id}`
              }
            >
              <span className="workspace-initial">
                {workspace.name.slice(0, 1)}
              </span>
              <h2>{workspace.name}</h2>
              <span>
                {workspace.status === "initializing"
                  ? t("Continue setup", "המשך ההגדרה")
                  : workspace.role === "owner"
                    ? t("Workspace owner", "בעלים של המרחב")
                    : t("Family member", "חבר במרחב")}
                <Arrow />
              </span>
            </Link>
          ))}
          <Link className="workspace-card create" to="/app/new">
            <Plus />
            <h2>{t("Begin another story", "התחילו סיפור נוסף")}</h2>
            <span>
              {t("Create a workspace", "יצירת מרחב")}
              <Arrow />
            </span>
          </Link>
        </div>
      </main>
    </div>
  );
}
export function Swatches({
  primary,
  secondary,
  onChange,
  disabled = false,
}: {
  primary: string;
  secondary: string;
  onChange: (primary: string, secondary: string) => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className="swatch-fields">
      {[
        {
          title: t("Primary colour", "צבע ראשי"),
          items: PRIMARY_COLORS,
          value: primary,
          isPrimary: true,
        },
        {
          title: t("Secondary colour", "צבע משני"),
          items: SECONDARY_COLORS,
          value: secondary,
          isPrimary: false,
        },
      ].map((group) => (
        <fieldset key={group.title}>
          <legend>{group.title}</legend>
          <div className="swatches">
            {group.items.map((color) => (
              <button
                type="button"
                key={color.value}
                disabled={disabled}
                aria-pressed={group.value === color.value}
                aria-label={t(color.en, color.he)}
                title={t(color.en, color.he)}
                onClick={() =>
                  onChange(
                    group.isPrimary ? color.value : primary,
                    group.isPrimary ? secondary : color.value,
                  )
                }
              >
                <span
                  style={{
                    background: color.value,
                    color: group.isPrimary ? "white" : "#24251d",
                  }}
                >
                  {group.value === color.value && <Check size={19} />}
                </span>
                <small>{t(color.en, color.he)}</small>
              </button>
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
export function Onboarding() {
  const { wid } = useParams();
  const { refresh } = useAuth();
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [primary, setPrimary] = useState("#284b40");
  const [secondary, setSecondary] = useState("#b4a16b");
  const [people, setPeople] = useState<Person[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [personName, setPersonName] = useState("");
  const [hebrewName, setHebrewName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [relative, setRelative] = useState("");
  const [relation, setRelation] = useState("parent");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(false);
  const [loaded, setLoaded] = useState(!wid);
  const [creationId] = useState(() => requestId());
  const personRequestId = useRef(requestId());
  const [saveStatus, setSaveStatus] = useState("");
  useEffect(() => {
    if (!wid) return;
    Promise.all([
      api<{ workspace: Workspace }>(`/workspaces/${wid}`),
      api<{ items: Person[] }>(`/workspaces/${wid}/people?limit=100`),
      api<{ items: Relationship[] }>(
        `/workspaces/${wid}/relationships?limit=100`,
      ),
    ])
      .then(([result, list, connections]) => {
        if (result.workspace.status === "ready") {
          navigate(`/app/w/${wid}`, { replace: true });
          return;
        }
        setWorkspace(result.workspace);
        setStep(result.workspace.onboardingStep);
        setName(result.workspace.name);
        setPrimary(result.workspace.primaryColor);
        setSecondary(result.workspace.secondaryColor);
        setPeople(list.items);
        setRelationships(connections.items);
        setLoaded(true);
      })
      .catch((e) => {
        setError(friendlyError(e));
        setLoaded(true);
      });
  }, [wid, navigate]);
  async function next() {
    setBusy(true);
    setError("");
    try {
      if (!workspace) {
        const result = await post<{ workspace: Workspace }>("/workspaces", {
          name: name.trim(),
          requestId: creationId,
        });
        setWorkspace(result.workspace);
        const saved = await patch<{ workspace: Workspace }>(
          `/workspaces/${result.workspace.id}/settings`,
          { expectedVersion: result.workspace.version, onboardingStep: 2 },
        );
        setWorkspace(saved.workspace);
        setStep(2);
        navigate(`/app/setup/${result.workspace.id}`, { replace: true });
      } else {
        const saved = await patch<{ workspace: Workspace }>(
          `/workspaces/${workspace.id}/settings`,
          {
            expectedVersion: workspace.version,
            name: name.trim(),
            primaryColor: primary,
            secondaryColor: secondary,
            onboardingStep: Math.min(step + 1, 3),
          },
        );
        setWorkspace(saved.workspace);
        setStep(Math.min(step + 1, 3));
      }
    } catch (e) {
      setError(friendlyError(e, language === "he"));
    } finally {
      setBusy(false);
    }
  }
  async function addPerson(e: React.FormEvent) {
    e.preventDefault();
    if (!workspace) return;
    setBusy(true);
    setError("");
    try {
      const result = await post<{ item: Person; relationship?: Relationship }>(
        `/workspaces/${workspace.id}/people`,
        {
          requestId: personRequestId.current,
          data: {
            name: personName.trim(),
            hebrewName: hebrewName.trim(),
            birthDate: birthDate.trim(),
          },
          ...(relative
            ? { relationship: { relativeId: relative, type: relation } }
            : {}),
        },
      );
      setPeople((old) =>
        old.some((person) => person.id === result.item.id)
          ? old
          : [...old, result.item],
      );
      if (result.relationship)
        setRelationships((old) =>
          old.some((item) => item.id === result.relationship!.id)
            ? old
            : [...old, result.relationship!],
        );
      personRequestId.current = requestId();
      setPersonName("");
      setHebrewName("");
      setBirthDate("");
      setRelative("");
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }
  async function saveColours(p: string, s: string) {
    if (!workspace) return;
    setPrimary(p);
    setSecondary(s);
    setBusy(true);
    setError("");
    setSaveStatus(t("Saving…", "שומרים…"));
    try {
      const result = await patch<{ workspace: Workspace }>(
        `/workspaces/${workspace.id}/settings`,
        {
          expectedVersion: workspace.version,
          primaryColor: p,
          secondaryColor: s,
          onboardingStep: 2,
        },
      );
      setWorkspace(result.workspace);
      setSaveStatus(t("Your colours are saved", "הצבעים שלכם נשמרו"));
    } catch (e) {
      setError(friendlyError(e));
      setPrimary(workspace.primaryColor);
      setSecondary(workspace.secondaryColor);
      setSaveStatus("");
    } finally {
      setBusy(false);
    }
  }
  async function finish() {
    if (!workspace) return;
    setBusy(true);
    setError("");
    try {
      await post(`/workspaces/${workspace.id}/complete`, {
        expectedVersion: workspace.version,
      });
      await refresh();
      navigate(`/app/w/${workspace.id}`);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }
  if (!loaded) return <Loading />;
  return (
    <div className="setup-shell" style={themeVariables(primary, secondary)}>
      <SetupHeader />
      <main className="onboarding">
        <div className="setup-intro">
          <span className="eyebrow">
            {t("A HOME FOR YOUR FAMILY", "בית למשפחה שלכם")}
          </span>
          <h1>
            {t("Great stories begin", "סיפורים גדולים מתחילים")}
            <em>{t(" with a few small steps.", " בכמה צעדים קטנים.")}</em>
          </h1>
          <p>
            {t(
              "Your progress is saved to your account. Come back whenever you are ready.",
              "ההתקדמות נשמרת בחשבון שלכם. תוכלו לחזור בכל עת.",
            )}
          </p>
          <ol className="setup-steps">
            {[
              t("Name your workspace", "שם למרחב"),
              t("Make it feel like yours", "הצבעים שלכם"),
              t("Plant the first roots", "שורשים ראשונים"),
            ].map((label, index) => (
              <li
                className={
                  step === index + 1
                    ? "current"
                    : step > index + 1
                      ? "complete"
                      : ""
                }
                aria-current={step === index + 1 ? "step" : undefined}
                key={label}
              >
                <span>
                  {step > index + 1 ? <Check size={16} /> : index + 1}
                </span>
                {label}
              </li>
            ))}
          </ol>
        </div>
        <section className="setup-card">
          <div className="eyebrow">
            {t(`STEP ${step} OF 3`, `שלב ${step} מתוך 3`)}
          </div>
          <h2>
            {step === 1
              ? t("What shall we call home?", "איך נקרא לבית שלכם?")
              : step === 2
                ? t("Your family, your colours.", "המשפחה שלכם, הצבעים שלכם.")
                : summary
                  ? t("Your first chapter is ready.", "הפרק הראשון שלכם מוכן.")
                  : t(
                      "Begin with someone you know.",
                      "התחילו באדם שאתם מכירים.",
                    )}
          </h2>
          <ErrorBox error={error} />
          {step === 1 ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void next();
              }}
            >
              <p>
                {t(
                  "Choose a name everyone will recognize. You can change it later.",
                  "בחרו שם שכולם יזהו. תוכלו לשנות אותו בהמשך.",
                )}
              </p>
              <Field label={t("Workspace name", "שם המרחב")}>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  required
                  autoFocus
                  placeholder={t("Your family’s name", "שם המשפחה שלכם")}
                />
              </Field>
              <Button disabled={busy || !name.trim()}>
                {busy
                  ? t("Saving…", "שומרים…")
                  : t("Next: your colours", "הבא: הצבעים שלכם")}
                <Arrow />
              </Button>
            </form>
          ) : step === 2 ? (
            <>
              <p>
                {t(
                  "A familiar feeling, from the very first visit. Your choices apply to everyone in this workspace.",
                  "תחושה מוכרת כבר מהביקור הראשון. הבחירות שלכם יחולו על כל חברי המרחב.",
                )}
              </p>
              <Swatches
                primary={primary}
                secondary={secondary}
                disabled={busy}
                onChange={(p, s) => void saveColours(p, s)}
              />
              <p className="save-status" role="status">
                {saveStatus}
              </p>
              <div className="theme-preview">
                <Leaf size={30} />
                <span>
                  {name}
                  <small>
                    {t(
                      "Your family, beautifully connected.",
                      "המשפחה שלכם, מחוברת באהבה.",
                    )}
                  </small>
                </span>
              </div>
              <div className="form-actions">
                <Button
                  kind="secondary"
                  disabled={busy}
                  onClick={() => setStep(1)}
                >
                  <ArrowLeft size={16} />
                  {t("Back", "חזרה")}
                </Button>
                <Button disabled={busy} onClick={() => void next()}>
                  {busy
                    ? t("Saving…", "שומרים…")
                    : t("Next: your first people", "הבא: האנשים הראשונים")}
                  <Arrow />
                </Button>
              </div>
            </>
          ) : summary ? (
            <>
              <div className="completion-mark">
                <Check size={32} />
              </div>
              <p>
                {t(
                  "Your workspace and first family records are saved. Everyone you invite will share this same private archive.",
                  "המרחב והרשומות המשפחתיות הראשונות נשמרו. כל מי שתזמינו ישתף את אותו הארכיון הפרטי.",
                )}
              </p>
              <div className="summary-line">
                <span>{t("Workspace", "מרחב")}</span>
                <b>{name}</b>
              </div>
              <div className="summary-line">
                <span>
                  {t("People in your family tree", "אנשים בעץ המשפחה")}
                </span>
                <b>{people.length}</b>
              </div>
              <div className="summary-line">
                <span>{t("Saved colours", "צבעים שנשמרו")}</span>
                <b className="summary-colours">
                  <i style={{ background: primary }} />
                  {t(
                    PRIMARY_COLORS.find((c) => c.value === primary)?.en || "",
                    PRIMARY_COLORS.find((c) => c.value === primary)?.he || "",
                  )}{" "}
                  · <i style={{ background: secondary }} />
                  {t(
                    SECONDARY_COLORS.find((c) => c.value === secondary)?.en ||
                      "",
                    SECONDARY_COLORS.find((c) => c.value === secondary)?.he ||
                      "",
                  )}
                </b>
              </div>
              <div className="summary-line">
                <span>{t("Saved relationships", "קשרים שנשמרו")}</span>
                <b>{relationships.length}</b>
              </div>
              <SetupTree people={people} relationships={relationships} />
              <div className="form-actions">
                <Button
                  kind="secondary"
                  disabled={busy}
                  onClick={() => setSummary(false)}
                >
                  {t("Back", "חזרה")}
                </Button>
                <Button disabled={busy} onClick={() => void finish()}>
                  {busy
                    ? t("Finishing…", "מסיימים…")
                    : t("Finish & enter workspace", "סיום וכניסה למרחב")}
                  <Arrow />
                </Button>
              </div>
            </>
          ) : (
            <>
              <p>
                {t(
                  "Add at least one real person. Names and dates can stay in their original language; unknown details can wait.",
                  "הוסיפו לפחות אדם אמיתי אחד. שמות ותאריכים יכולים להישאר בשפת המקור; פרטים לא ידועים יכולים לחכות.",
                )}
              </p>
              {people.length > 0 && (
                <SetupTree people={people} relationships={relationships} />
              )}{" "}
              {people.length > 0 && (
                <ul className="saved-people">
                  {people.map((p) => (
                    <li key={p.id}>
                      <span className="avatar small">{p.name[0]}</span>
                      {p.name}
                      <Check size={15} />
                    </li>
                  ))}
                </ul>
              )}
              <form onSubmit={addPerson}>
                <div className="field-row">
                  <Field label={t("Full name", "שם מלא")}>
                    <input
                      value={personName}
                      onChange={(e) => setPersonName(e.target.value)}
                      required
                      maxLength={160}
                    />
                  </Field>
                  <Field
                    label={t("Hebrew name (optional)", "שם בעברית (רשות)")}
                  >
                    <input
                      value={hebrewName}
                      onChange={(e) => setHebrewName(e.target.value)}
                      dir="auto"
                    />
                  </Field>
                </div>
                <Field
                  label={t(
                    "Birth date or approximate year (optional)",
                    "תאריך לידה או שנה משוערת (רשות)",
                  )}
                >
                  <input
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    placeholder={t(
                      "For example, around 1950",
                      "לדוגמה, בערך 1950",
                    )}
                  />
                </Field>
                {people.length > 0 && (
                  <div className="field-row">
                    <Field
                      label={t("Connect to (optional)", "חיבור לאדם (רשות)")}
                    >
                      <select
                        value={relative}
                        onChange={(e) => setRelative(e.target.value)}
                      >
                        <option value="">
                          {t("No relationship yet", "ללא קשר בשלב זה")}
                        </option>
                        {people.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                    {relative && (
                      <Field
                        label={t(
                          "Selected person is their…",
                          "האדם שנבחר הוא…",
                        )}
                      >
                        <select
                          value={relation}
                          onChange={(e) => setRelation(e.target.value)}
                        >
                          <option value="parent">{t("Parent", "הורה")}</option>
                          <option value="child">{t("Child", "ילד/ה")}</option>
                          <option value="partner">
                            {t("Partner", "בן/בת זוג")}
                          </option>
                        </select>
                      </Field>
                    )}
                  </div>
                )}
                <Button kind="secondary" disabled={busy || !personName.trim()}>
                  <Plus size={16} />
                  {t("Save person", "שמירת אדם")}
                </Button>
              </form>
              <div className="form-actions">
                <Button kind="ghost" disabled={busy} onClick={() => setStep(2)}>
                  {t("Back", "חזרה")}
                </Button>
                <Button
                  disabled={busy || people.length === 0}
                  onClick={() => setSummary(true)}
                >
                  {t("Review & finish", "בדיקה וסיום")}
                  <Arrow />
                </Button>
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
export function Invitation() {
  const [params] = useSearchParams();
  const token =
    params.get("token") || sessionStorage.getItem("heritage-invitation");
  const { user, loading, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (token) sessionStorage.setItem("heritage-invitation", token);
  }, [token]);
  async function accept() {
    setBusy(true);
    setError("");
    try {
      const result = await post<{ workspace: Workspace }>(
        "/invitations/accept",
        { token },
      );
      setWorkspace(result.workspace);
      sessionStorage.removeItem("heritage-invitation");
      await refresh();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }
  if (loading) return <Loading />;
  if (!user)
    return (
      <Navigate
        to={`/signup?invite=${encodeURIComponent(token || "")}`}
        replace
      />
    );
  if (!user.emailVerified) return <Navigate to="/app" replace />;
  return (
    <div className="setup-shell">
      <SetupHeader />
      <main
        className="invite-card"
        style={
          workspace
            ? themeVariables(workspace.primaryColor, workspace.secondaryColor)
            : undefined
        }
      >
        <Users size={42} strokeWidth={1} />
        <span className="eyebrow">
          {t("YOU BELONG HERE", "כאן אתם שייכים")}
        </span>
        <h1>
          {workspace
            ? `${t("Welcome to", "ברוכים הבאים אל")} ${workspace.name}`
            : t("A family story awaits you.", "סיפור משפחתי מחכה לכם.")}
        </h1>
        {workspace ? (
          <>
            <p>
              {t(
                "This is a shared family archive. You can add photographs, grow the family tree, tell stories, and edit or restore any family content. Workspace owners take care of invitations and settings.",
                "זהו ארכיון משפחתי משותף. תוכלו להוסיף תצלומים, להרחיב את עץ המשפחה, לספר סיפורים ולערוך או לשחזר תוכן משפחתי. בעלי המרחב מטפלים בהזמנות ובהגדרות.",
              )}
            </p>
            <div className="orientation-icons">
              <Users />
              <GitBranch />
              <Leaf />
            </div>
            <Button onClick={() => navigate(`/app/w/${workspace.id}`)}>
              {t("Explore your family workspace", "כניסה למרחב המשפחתי")}
              <Arrow />
            </Button>
          </>
        ) : (
          <>
            <p>
              {t(
                "Accept this invitation using the verified email address it was sent to. Your existing workspaces stay separate.",
                "אשרו את ההזמנה באמצעות כתובת הדוא״ל המאומתת שאליה נשלחה. המרחבים הקיימים שלכם יישארו נפרדים.",
              )}
            </p>
            <ErrorBox
              error={
                error ||
                (!token
                  ? t(
                      "This invitation link is incomplete. Ask the owner for a new link.",
                      "קישור ההזמנה אינו מלא. בקשו מבעלי המרחב קישור חדש.",
                    )
                  : "")
              }
            />
            <Button disabled={busy || !token} onClick={() => void accept()}>
              {busy
                ? t("Joining…", "מצטרפים…")
                : t("Accept invitation", "אישור ההזמנה")}
              <Arrow />
            </Button>
            {error && (
              <button
                className="text-link"
                onClick={() => {
                  sessionStorage.removeItem("heritage-invitation");
                  navigate("/app/new");
                }}
              >
                {t("Create my own workspace instead", "יצירת מרחב משלי")}
              </button>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function SetupTree({
  people,
  relationships,
}: {
  people: Person[];
  relationships: Relationship[];
}) {
  const { t } = useI18n();
  const layout = layoutTree(people, relationships);
  const factor = 0.62;
  return (
    <div
      className="setup-tree"
      role="region"
      tabIndex={0}
      aria-label={t(
        "Your saved family tree preview",
        "תצוגה מקדימה של עץ המשפחה השמור",
      )}
    >
      <div
        style={{
          width: Math.max(300, layout.width * factor),
          height: Math.max(170, layout.height * factor),
          position: "relative",
        }}
      >
        <svg
          width={layout.width * factor}
          height={layout.height * factor}
          aria-hidden="true"
        >
          {relationships.map((r) => {
            const a = layout.positions.get(r.fromPersonId),
              b = layout.positions.get(r.toPersonId);
            return a && b ? (
              <path
                key={r.id}
                d={`M ${a.x * factor} ${a.y * factor + 25} V ${((a.y + b.y) * factor) / 2} H ${b.x * factor} V ${b.y * factor}`}
                stroke="currentColor"
                fill="none"
              />
            ) : null;
          })}
        </svg>
        {people.map((p) => {
          const pos = layout.positions.get(p.id)!;
          return (
            <div
              className="setup-tree-person"
              style={{ left: pos.x * factor, top: pos.y * factor }}
              key={p.id}
            >
              <span className="avatar small">{p.name[0]}</span>
              <b>{p.name}</b>
            </div>
          );
        })}
      </div>
    </div>
  );
}
